import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { appPaths } from "../../../shared/routing/appPaths";
import type { SourceDocumentSummary } from "../../../api/content/sourcesApi";
import AppButton from "../../../ui/buttons/AppButton";
import EmptyState from "../../../ui/feedback/EmptyState";
import { AttachedStatus, shownKey, useKeyForm } from "../../search";
import { classNames } from "../../../ui/styles/classNames";

interface DocumentListProps {
  /** The search's attached-data filter narrows the list. */
  filtered: boolean;
  documents: readonly SourceDocumentSummary[];
  selectedDocumentId: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}

/**
 * One list of documents (UX-2): no split by processing stage, no delete actions (UX-12).
 * Each links to the exact version listed, so the link opens the same text later (NFR-16),
 * and shows its most developed form (F88), as v1's drawer did, and its key in the form chosen
 * (F136). Keys and forms are never cut off; a form that doesn't fit beside its key goes
 * beneath it (F137).
 */
export default function DocumentList({
  filtered,
  documents,
  selectedDocumentId,
  hasMore,
  loadingMore,
  onLoadMore,
}: DocumentListProps) {
  const { t } = useTranslation();
  const keyForm = useKeyForm();

  if (documents.length === 0) {
    return <EmptyState size="small">{t(filtered ? "sources.list.noneFiltered" : "sources.list.empty")}</EmptyState>;
  }

  return (
    <>
      <ul className="space-y-1">
        {documents.map((document) => (
          <li key={document.ref.documentId}>
            <Link
              to={appPaths.sourceDocument(document.ref)}
              className={classNames(
                "app-nav-item flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 px-3 py-2 text-sm font-semibold",
                document.ref.documentId === selectedDocumentId && "app-nav-item-active",
              )}
              aria-current={document.ref.documentId === selectedDocumentId ? "page" : undefined}
            >
              <span className="min-w-0 [overflow-wrap:anywhere]">{shownKey(document, keyForm)}</span>
              <AttachedStatus forms={document.attached} />
            </Link>
          </li>
        ))}
      </ul>
      {hasMore ? (
        <AppButton
          type="button"
          variant="secondary"
          size="sm"
          fullWidth={true}
          className="mt-3"
          disabled={loadingMore}
          onClick={onLoadMore}
        >
          {loadingMore ? t("common.loading") : t("sources.list.loadMore")}
        </AppButton>
      ) : null}
    </>
  );
}
